/**
 * The editable-field registry.
 *
 * One description of every editable thing on the site, shared by three
 * consumers that must never disagree:
 *
 *   - the renderer, which stamps `data-agenz-edit="<id>"` onto the element
 *   - the in-page overlay, which highlights those elements and reports clicks
 *   - the admin panel, which decides what editor to show for the clicked id
 *
 * Keeping it in one place is the whole trick. If the admin held its own list,
 * adding a field would mean editing it in two places and the two would drift
 * silently — the editor would offer fields the site does not render, or the
 * site would show pencil icons that open nothing.
 *
 * `path` is the dotted route into the SiteSettings payload, which is also the
 * shape the PATCH endpoint accepts, so saving needs no per-field mapping.
 */

/** Field types the admin knows how to render an editor for. */
export const FIELD_TYPES = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  LIST: 'list',
  STEPS: 'steps',
  COLOR: 'color',
  NUMBER: 'number',
};

const f = (id, label, type, page, extra = {}) => ({
  id,
  label,
  type,
  page,
  path: id.split('.'),
  ...extra,
});

/**
 * Ordered so the admin's outline panel reads top-to-bottom the way the page
 * does — someone scanning for "the bit under the headline" should find it in
 * the same relative position.
 */
export const EDITABLE_FIELDS = [
  // ── Hero ──────────────────────────────────────────────────────────────────
  f('heroCopy.line1', 'Headline line 1', FIELD_TYPES.TEXT, '/', { group: 'Hero' }),
  f('heroCopy.line2', 'Headline line 2', FIELD_TYPES.TEXT, '/', { group: 'Hero' }),
  f('heroCopy.emphasis', 'Underlined word', FIELD_TYPES.TEXT, '/', { group: 'Hero' }),
  f('heroCopy.sub', 'Sub-headline', FIELD_TYPES.TEXTAREA, '/', { group: 'Hero' }),
  f('heroCopy.primaryLabel', 'Primary button', FIELD_TYPES.TEXT, '/', { group: 'Hero' }),
  f('heroCopy.secondaryLabel', 'Secondary button', FIELD_TYPES.TEXT, '/', { group: 'Hero' }),
  f('pageTones.home.surface', 'Hero background', FIELD_TYPES.COLOR, '/', { group: 'Hero' }),

  // ── Ticker ────────────────────────────────────────────────────────────────
  f('sections.marquee.items', 'Ticker phrases', FIELD_TYPES.LIST, '/', {
    group: 'Ticker',
    itemLabel: 'Phrase',
    reorderable: true,
  }),
  f('sections.marquee.speed', 'Ticker speed (s)', FIELD_TYPES.NUMBER, '/', { group: 'Ticker' }),

  // ── About ─────────────────────────────────────────────────────────────────
  f('sections.about.eyebrow', 'Eyebrow', FIELD_TYPES.TEXT, '/', { group: 'About' }),
  f('sections.about.headline', 'Headline', FIELD_TYPES.TEXT, '/', { group: 'About' }),
  f('sections.about.headlineAccent', 'Headline accent line', FIELD_TYPES.TEXT, '/', { group: 'About' }),
  f('sections.about.body', 'Body', FIELD_TYPES.TEXTAREA, '/', { group: 'About' }),

  // ── Mission ───────────────────────────────────────────────────────────────
  f('sections.mission.eyebrow', 'Eyebrow', FIELD_TYPES.TEXT, '/', { group: 'Mission' }),
  f('sections.mission.headline', 'Headline', FIELD_TYPES.TEXT, '/', { group: 'Mission' }),
  f('sections.mission.body', 'Body', FIELD_TYPES.TEXTAREA, '/', { group: 'Mission' }),
  f('sections.mindset.principles', 'Mindset principles', FIELD_TYPES.LIST, '/', {
    group: 'Mission',
    itemLabel: 'Principle',
    reorderable: true,
    multiline: true,
  }),

  // ── Services ──────────────────────────────────────────────────────────────
  f('sections.servicesIndex.homeHeading', 'Home heading', FIELD_TYPES.TEXT, '/', { group: 'Services' }),
  f('sections.servicesIndex.title', 'Services page title', FIELD_TYPES.TEXT, '/services', { group: 'Services' }),
  f('pageTones.services.surface', 'Services hero background', FIELD_TYPES.COLOR, '/services', { group: 'Services' }),

  // ── Work ──────────────────────────────────────────────────────────────────
  f('sections.workIndex.homeHeading', 'Home heading', FIELD_TYPES.TEXT, '/', { group: 'Work' }),
  f('sections.workIndex.title', 'Work page title', FIELD_TYPES.TEXT, '/work', { group: 'Work' }),
  f('sections.workIndex.lead', 'Work page lead', FIELD_TYPES.TEXTAREA, '/work', { group: 'Work' }),
  f('pageTones.work.surface', 'Work hero background', FIELD_TYPES.COLOR, '/work', { group: 'Work' }),

  // ── Process ───────────────────────────────────────────────────────────────
  f('sections.process.eyebrow', 'Eyebrow', FIELD_TYPES.TEXT, '/', { group: 'Process' }),
  f('sections.process.steps', 'Process steps', FIELD_TYPES.STEPS, '/', {
    group: 'Process',
    reorderable: true,
  }),
  f('pageTones.process.surface', 'Process hero background', FIELD_TYPES.COLOR, '/process', { group: 'Process' }),

  // ── Why ───────────────────────────────────────────────────────────────────
  f('sections.whyAgenz.eyebrow', 'Eyebrow', FIELD_TYPES.TEXT, '/', { group: 'Why AGENZ' }),
  f('sections.whyAgenz.headline', 'Headline', FIELD_TYPES.TEXT, '/', { group: 'Why AGENZ' }),
  f('sections.whyAgenz.points', 'Reasons', FIELD_TYPES.LIST, '/', {
    group: 'Why AGENZ',
    itemLabel: 'Reason',
    reorderable: true,
    multiline: true,
  }),

  // ── Closing CTA ───────────────────────────────────────────────────────────
  f('cta.headline', 'CTA headline', FIELD_TYPES.TEXT, '/', { group: 'Closing CTA' }),
  f('cta.sub', 'CTA supporting line', FIELD_TYPES.TEXTAREA, '/', { group: 'Closing CTA' }),
  f('cta.buttonLabel', 'CTA button', FIELD_TYPES.TEXT, '/', { group: 'Closing CTA' }),

  // ── About page ────────────────────────────────────────────────────────────
  f('sections.vision.eyebrow', 'Vision eyebrow', FIELD_TYPES.TEXT, '/about', { group: 'About page' }),
  f('sections.vision.headline', 'Vision headline', FIELD_TYPES.TEXTAREA, '/about', { group: 'About page' }),
  f('sections.vision.body', 'Vision body', FIELD_TYPES.TEXTAREA, '/about', { group: 'About page' }),
  f('pageTones.about.surface', 'About hero background', FIELD_TYPES.COLOR, '/about', { group: 'About page' }),

  // ── Creative execution ────────────────────────────────────────────────────
  f('sections.creativeExecution.eyebrow', 'Eyebrow', FIELD_TYPES.TEXT, '/services', { group: 'Creative execution' }),
  f('sections.creativeExecution.body', 'Body', FIELD_TYPES.TEXTAREA, '/services', { group: 'Creative execution' }),

  // ── Contact ───────────────────────────────────────────────────────────────
  f('contactForm.heading', 'Form heading', FIELD_TYPES.TEXT, '/contact', { group: 'Contact' }),
  f('contactForm.intro', 'Form intro', FIELD_TYPES.TEXTAREA, '/contact', { group: 'Contact' }),
  f('contactForm.submitLabel', 'Submit button', FIELD_TYPES.TEXT, '/contact', { group: 'Contact' }),
  f('contactForm.budgetBands', 'Budget options', FIELD_TYPES.LIST, '/contact', {
    group: 'Contact',
    itemLabel: 'Range',
    reorderable: true,
  }),
  f('pageTones.contact.surface', 'Contact hero background', FIELD_TYPES.COLOR, '/contact', { group: 'Contact' }),

  // ── Footer ────────────────────────────────────────────────────────────────
  f('sections.footer.headline', 'Footer headline', FIELD_TYPES.TEXT, '*', { group: 'Footer' }),
  f('sections.footer.sub', 'Footer sub-line', FIELD_TYPES.TEXTAREA, '*', { group: 'Footer' }),
];

const BY_ID = new Map(EDITABLE_FIELDS.map((field) => [field.id, field]));

export function getField(id) {
  return BY_ID.get(id) ?? null;
}

/** Fields grouped for the admin outline, preserving declaration order. */
export function fieldsByGroup(page) {
  const groups = new Map();
  for (const field of EDITABLE_FIELDS) {
    if (page && field.page !== '*' && field.page !== page) continue;
    if (!groups.has(field.group)) groups.set(field.group, []);
    groups.get(field.group).push(field);
  }
  return [...groups.entries()].map(([group, fields]) => ({ group, fields }));
}

/** Reads a dotted path out of the settings payload. */
export function readPath(source, path) {
  return path.reduce((acc, key) => (acc == null ? acc : acc[key]), source);
}

/**
 * Builds the minimal nested patch for a single field.
 *
 * The settings endpoint merges at the top level, so a change to
 * `sections.about.headline` has to carry the whole `sections` object or the
 * other sections would be dropped. `base` supplies the current values.
 */
export function buildPatch(field, value, base = {}) {
  const [root, ...rest] = field.path;
  if (rest.length === 0) return { [root]: value };

  const cloned = structuredClone(base?.[root] ?? {});
  let cursor = cloned;
  for (let i = 0; i < rest.length - 1; i += 1) {
    cursor[rest[i]] = cursor[rest[i]] ?? {};
    cursor = cursor[rest[i]];
  }
  cursor[rest.at(-1)] = value;

  return { [root]: cloned };
}

export default EDITABLE_FIELDS;
